import moment, { unitOfTime } from 'moment';

export namespace DateUtils {
    export function toLocal(date: string | Date) {
        return moment(date).toDate();
    }

    export type FormatOptions = {
        withAt?: boolean;
        emptyText?: string;
        hours?: boolean;
        minutes?: boolean;
        seconds?: boolean;
        milliseconds?: boolean;
    };

    export function format(date: string | Date | null | undefined, options?: FormatOptions) {
        const {
            withAt = true,
            emptyText = 'N/A',
            hours = true,
            minutes = true,
            seconds = true,
            milliseconds = false,
        } = options || {};

        if (!date) {
            return emptyText || date;
        }

        const timeParts = [hours && 'HH', minutes && 'mm', seconds && 'ss']
            .filter((part) => !!part)
            .join(':');

        const partMilliseconds = milliseconds ? '.SSS' : '';
        return moment(date).format(
            `YYYY-MM-DD${withAt && timeParts ? '[ at]' : ''} ${timeParts}${partMilliseconds}`,
        );
    }

    export function formatTime(date: string | Date, options = { milliseconds: false }) {
        const partMilliseconds = options?.milliseconds ? '.SSS' : '';
        return moment(date).format(`HH:mm:ss${partMilliseconds}`);
    }

    export type FormatDurationProps = {
        emptyText?: string;
        units?: unitOfTime.Base | unitOfTime.Base[];
    };

    function getDurationUnitText(unit: unitOfTime.Base) {
        return unit === 'milliseconds' ? 'ms' : unit[0];
    }

    function formatDurationPart(value: number, unit: unitOfTime.Base) {
        return value + '' + getDurationUnitText(unit);
    }

    export function formatDuration(
        from: Date | string | null | undefined,
        to: Date | string | null | undefined,
        { emptyText = 'N/A', units }: FormatDurationProps = {},
    ) {
        if (!from || !to) {
            return emptyText || '';
        }

        const duration = moment.duration(moment(to).diff(from));

        if (units && !Array.isArray(units)) {
            return formatDurationPart(duration.as(units), units);
        } else {
            units = units || [
                'years',
                'months',
                'weeks',
                'days',
                'hours',
                'minutes',
                'seconds',
                'milliseconds',
            ];

            const values = units.reduce((res, unit) => {
                const text = unit[0].toLowerCase();
                const value = duration.get(unit);
                if (value >= 1) {
                    res.push(formatDurationPart(value, unit));
                }
                return res;
            }, [] as string[]);

            const valuesToShow = values.slice(0, 3);
            if (values.length > valuesToShow.length) {
                const lastIndex = valuesToShow.length - 1;
                valuesToShow[lastIndex] = valuesToShow[lastIndex] + '+';
            }

            const formatted = valuesToShow.join(' ') || duration.as('milliseconds') + 'ms';
            return formatted;
        }
    }
}
